import React, { useState, useRef, useMemo } from 'react';
import { f0, fc, fp, ord, calcPayoff, svgLine } from '../utils';
import { createPost } from '../api';

const COLORS = ['#ff6b6b', '#4d96ff', '#ffd93d', '#6bcb77', '#ff6bdf', '#7C6FF7', '#f0a500'];

export default function Debt({ state, updateState, showToast, triggerConfetti }) {
  const [editId, setEditId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [confirmPO, setConfirmPO] = useState(null);
  const [showChart, setShowChart] = useState(true);
  const [showSchedule, setShowSchedule] = useState(true);
  const [debtCustomOpen, setDebtCustomOpen] = useState(false);
  const [whatIfExtra, setWhatIfExtra] = useState(null);
  const [payoffCelebration, setPayoffCelebration] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const editRefs = useRef({});
  const [newCard, setNewCard] = useState({
    name: '',
    bal: '',
    apr: '',
    min: '',
    due: '',
    col: COLORS[0],
  });

  const { cards, extra, method, lump } = state;
  const archivedIds = state.archivedCardIds || [];
  const activeCards = (cards || []).filter(c => !archivedIds.includes(c.id));
  const archivedCards = (cards || []).filter(c => archivedIds.includes(c.id));
  const archiveCard = (id) => updateState({ archivedCardIds: [...archivedIds, id] });
  const unarchiveCard = (id) => updateState({ archivedCardIds: archivedIds.filter(x => x !== id) });
  const r = useMemo(() => calcPayoff(activeCards, extra, method, lump), [activeCards, extra, method, lump]);
  const m = useMemo(() => calcPayoff(activeCards, 0, 'min'), [activeCards]);
  const wiExtra = whatIfExtra ?? extra;
  const wiMax = Math.max(extra + 500, 1000);
  const wi = useMemo(() => calcPayoff(activeCards, wiExtra, method, lump), [activeCards, wiExtra, method, lump]);
  const rNL = useMemo(() => calcPayoff(activeCards, extra, method, 0), [activeCards, extra, method]);
  const totMin = activeCards.reduce((s, c) => s + +c.min, 0);

  const snowOrder = (cards || [])
    .slice()
    .sort((a, b) => a.bal - b.bal)
    .map((c) => c.name)
    .join(' → ');
  const avalOrder = (cards || [])
    .slice()
    .sort((a, b) => b.apr - a.apr)
    .map((c) => c.name)
    .join(' → ');
  const firstP =
    method === 'snowball'
      ? (cards || []).slice().sort((a, b) => a.bal - b.bal)[0]
      : (cards || []).slice().sort((a, b) => b.apr - a.apr)[0];

  const saveEdit = () => {
    const updated = (cards || []).map((c) => {
      if (c.id !== editId) return c;
      const get = (f) => parseFloat(editRefs.current[f]?.value) || c[f];
      return {
        ...c,
        bal: get('bal'),
        apr: get('apr'),
        min: get('min'),
        due: Math.round(get('due')),
      };
    });
    updateState({ cards: updated });
    setEditId(null);
    showToast('Updated!');
  };

  const addCard = () => {
    if (!newCard.name || !newCard.bal || !newCard.apr || !newCard.min || !newCard.due) {
      showToast('Fill in all fields');
      return;
    }
    const bal = parseFloat(newCard.bal);
    const apr = parseFloat(newCard.apr);
    const min = parseFloat(newCard.min);
    const due = parseInt(newCard.due);
    if (bal <= 0) { showToast('Balance must be greater than $0'); return; }
    if (apr < 0) { showToast('APR cannot be negative'); return; }
    if (min <= 0) { showToast('Minimum payment must be greater than $0'); return; }
    if (due < 1 || due > 31) { showToast('Due day must be between 1 and 31'); return; }
    const card = {
      id: 'card_' + Date.now(),
      name: newCard.name.trim(),
      bal,
      apr,
      min,
      due,
      col: newCard.col,
    };
    const updated = [...(cards || []), card];
    updateState({ cards: updated, initCards: [...(state.initCards || []), { ...card }] });
    setNewCard({ name: '', bal: '', apr: '', min: '', due: '', col: COLORS[0] });
    setAdding(false);
    showToast('Debt added!');
  };

  const removeCard = (id) => {
    if (!window.confirm('Remove this account?')) return;
    updateState({
      cards: (cards || []).filter((c) => c.id !== id),
      initCards: (state.initCards || []).filter((c) => c.id !== id),
    });
  };

  const payOffCard = (id) => setConfirmPO(id);
  const confirmPayOff = (id) => {
    const card = (cards || []).find(c => c.id === id);
    updateState({ cards: (cards || []).map((c) => (c.id === id ? { ...c, bal: 0 } : c)) });
    setConfirmPO(null);
    triggerConfetti?.();
    showToast('🎉 Paid off! Balance set to $0.');
    if (card) setPayoffCelebration({ cardName: card.name });
  };

  const cd = r.sched.filter((_, i) => i % 3 === 0 || i === r.sched.length - 1);
  const md = m.sched.filter((_, i) => i % 3 === 0 || i === m.sched.length - 1);
  const maxL = Math.max(cd.length, md.length);
  const merged = Array.from({ length: maxL }, (_, i) => ({
    lbl: (cd[i] || md[i]).lbl,
    plan: cd[i]?.tot ?? 0,
    min: md[i]?.tot ?? 0,
  }));
  const planLbl =
    (method === 'snowball' ? 'Snowball' : 'Avalanche') +
    (extra > 0 ? ' +' + f0(extra) + '/mo' : '') +
    (lump > 0 ? ' +' + f0(lump) + ' lump' : '');

  const sharePayoff = async (post) => {
    if (post) {
      await createPost({
        content: `🎉 Just paid off my ${payoffCelebration.cardName}! Debt free on that one! 💪`,
        postType: 'achievement',
        attachedStat: { label: `${payoffCelebration.cardName} — Paid Off!`, value: '🎊' },
      });
      showToast('Posted to feed! 🎉');
    }
    setPayoffCelebration(null);
  };

  return (
    <div>
      {/* ── Debt Payoff Celebration Modal ──────────────────────────────────── */}
      {payoffCelebration && (
        <div style={{ position: 'fixed', inset: 0, background: '#0009', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: 'var(--surf)', borderRadius: 20, padding: 32, maxWidth: 340, width: '100%', textAlign: 'center', boxShadow: '0 20px 60px #000a' }}>
            <div style={{ fontSize: 56, marginBottom: 12 }}>🎊</div>
            <div style={{ fontSize: 22, fontWeight: 900, marginBottom: 8 }}>Paid Off!</div>
            <div style={{ fontSize: 14, color: 'var(--mut)', marginBottom: 24 }}>
              You wiped out <strong style={{ color: 'var(--ylw)' }}>{payoffCelebration.cardName}</strong>!<br />
              Share this win with your friends?
            </div>
            <button onClick={() => sharePayoff(true)} style={{ width: '100%', padding: '12px', borderRadius: 12, background: 'var(--ylw)', color: '#000', fontWeight: 900, fontSize: 14, border: 'none', cursor: 'pointer', marginBottom: 10 }}>
              🌐 Post to Feed
            </button>
            <button onClick={() => sharePayoff(false)} style={{ width: '100%', padding: '10px', borderRadius: 12, background: 'none', border: '1px solid var(--bdr)', color: 'var(--mut)', fontSize: 13, cursor: 'pointer' }}>
              Keep it private
            </button>
          </div>
        </div>
      )}

      {/* ── What-If Simulator ──────────────────────────────────────────────── */}
      {(cards || []).length > 0 && r.mos > 0 && (
        <div className="card cp" style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>
            🎛️ What-If: Extra Payment
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <span style={{ fontSize: 12, color: 'var(--mut)', flexShrink: 0 }}>+$0</span>
            <input
              type="range" min="0" max={wiMax} step="10"
              value={wiExtra}
              onChange={(e) => setWhatIfExtra(+e.target.value)}
              style={{ flex: 1, accentColor: 'var(--grn)' }}
            />
            <span style={{ fontSize: 12, color: 'var(--mut)', flexShrink: 0 }}>+{fc(wiMax)}</span>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            {[0, 50, 100, 200, 500].map((v) => (
              <button key={v} onClick={() => setWhatIfExtra(v)} style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: 'pointer', border: `1.5px solid ${wiExtra === v ? 'var(--grn)' : 'var(--bdr)'}`, background: wiExtra === v ? 'rgba(107,203,119,.15)' : 'none', color: wiExtra === v ? 'var(--grn)' : 'var(--mut)' }}>
                {v === 0 ? 'No extra' : `+${fc(v)}`}
              </button>
            ))}
          </div>
          <div style={{ padding: '12px 14px', background: 'var(--surf2)', borderRadius: 10, display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--mut)' }}>Extra per month</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--grn)' }}>{fc(wiExtra)}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--mut)' }}>Payoff date</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: wi.mos < r.mos ? 'var(--grn)' : 'var(--txt)' }}>{wi.end || '—'}</div>
              {wi.mos < r.mos && <div style={{ fontSize: 10, color: 'var(--grn)', fontWeight: 700 }}>{r.mos - wi.mos} months sooner ↑</div>}
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--mut)' }}>Interest saved</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--grn)' }}>{r.int > wi.int ? fc(r.int - wi.int) : '—'}</div>
            </div>
          </div>
          {wiExtra !== extra && (
            <button
              onClick={() => { updateState({ extra: wiExtra }); showToast('Extra payment updated!'); }}
              style={{ width: '100%', marginTop: 10, padding: '9px', borderRadius: 10, background: 'var(--grn)', color: '#000', fontWeight: 800, fontSize: 13, border: 'none', cursor: 'pointer' }}
            >
              Apply {fc(wiExtra)}/mo Extra →
            </button>
          )}
        </div>
      )}

      <div className="dcg" style={{ marginBottom: 12 }}>
        {activeCards.map((c) => {
          const init = (state.initCards || []).find((x) => x.id === c.id) || c;
          const pct = Math.max(0, Math.round(((init.bal - c.bal) / init.bal) * 100));
          const eta = r.cpoff[c.id];
          const fields = [
            ['Balance ($)', 'bal'],
            ['APR (%)', 'apr'],
            ['Monthly Payment ($)', 'min'],
            ['Due Day', 'due'],
          ];
          if (editId === c.id)
            return (
              <div key={c.id} className="dc" style={{ borderColor: c.col }}>
                <div style={{ height: 3, background: c.col, margin: '-16px -18px 12px' }}></div>
                <div className="dcn" style={{ marginBottom: 10 }}>
                  <div className="dot" style={{ background: c.col }}></div>
                  {c.name}
                </div>
                {fields.map(([l, f]) => (
                  <div key={f} className="ef">
                    <label>{l}</label>
                    <input
                      type="number"
                      defaultValue={c[f]}
                      step={f === 'apr' ? '.01' : '1'}
                      ref={(el) => (editRefs.current[f] = el)}
                    />
                  </div>
                ))}
                <div className="ea">
                  <button className="bsave" onClick={saveEdit}>
                    Save
                  </button>
                  <button className="bcancel" onClick={() => setEditId(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            );
          const msc =
            pct >= 75 ? '#7C6FF7' : pct >= 50 ? 'var(--red)' : pct >= 25 ? 'var(--blu)' : '';
          const msl = pct >= 75 ? '75%' : pct >= 50 ? '50%' : pct >= 25 ? '25%' : '';
          return (
            <div key={c.id} className="dc">
              <div
                style={{
                  height: 3,
                  background: c.col,
                  margin: '-16px -18px 12px',
                  borderRadius: '14px 14px 0 0',
                }}
              ></div>
              {msl && (
                <div className="ms2" style={{ background: msc }}>
                  {msl}
                </div>
              )}
              <div className="dcn">
                <div className="dot" style={{ background: c.col }}></div>
                {c.name}
                <button className="ebt" onClick={() => setEditId(c.id)}>
                  ✏️
                </button>
              </div>
              <div className="dcb" style={{ color: c.col }}>
                {f0(c.bal)}
              </div>
              <div className="pw">
                <div className="pb" style={{ background: c.col, width: pct + '%' }}></div>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 10,
                  color: 'var(--mut)',
                  marginTop: 2,
                  marginBottom: 8,
                }}
              >
                <span>{pct}% paid</span>
                <span>{eta ? 'Free ' + eta.lbl : ''}</span>
              </div>
              <div className="dcs">
                <div className="dci">
                  <div className="dcil">APR</div>
                  <div className="dciv">{fp(c.apr)}</div>
                </div>
                <div className="dci">
                  <div className="dcil">Monthly Payment</div>
                  <div className="dciv">{f0(c.min)}</div>
                </div>
                <div className="dci">
                  <div className="dcil">Due Day</div>
                  <div className="dciv">{ord(c.due)} of mo</div>
                </div>
                <div className="dci">
                  <div className="dcil">Monthly Interest</div>
                  <div className="dciv">{fc((c.bal * c.apr) / 100 / 12)}</div>
                </div>
              </div>
              {confirmPO === c.id ? (
                <div style={{ display: 'flex', gap: 6, marginTop: 10, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: 'var(--mut)', flex: 1 }}>
                    Set balance to $0?
                  </span>
                  <button
                    onClick={() => confirmPayOff(c.id)}
                    style={{
                      padding: '6px 12px',
                      background: 'rgba(107,203,119,.2)',
                      border: '1px solid rgba(107,203,119,.5)',
                      borderRadius: 7,
                      color: 'var(--grn)',
                      fontSize: 11,
                      cursor: 'pointer',
                      fontWeight: 700,
                    }}
                  >
                    ✓ Confirm
                  </button>
                  <button
                    onClick={() => setConfirmPO(null)}
                    style={{
                      padding: '6px 10px',
                      background: 'none',
                      border: '1px solid var(--bdr)',
                      borderRadius: 7,
                      color: 'var(--mut)',
                      fontSize: 11,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                  {c.bal > 0 && (
                    <button
                      onClick={() => payOffCard(c.id)}
                      style={{ flex: 1, padding: '6px', background: 'rgba(107,203,119,.1)', border: '1px solid rgba(107,203,119,.3)', borderRadius: 7, color: 'var(--grn)', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}
                    >
                      ✓ Mark Paid Off
                    </button>
                  )}
                  {c.bal === 0 && (
                    <button
                      onClick={() => archiveCard(c.id)}
                      style={{ flex: 1, padding: '6px', background: 'rgba(107,203,119,.08)', border: '1px solid rgba(107,203,119,.25)', borderRadius: 7, color: 'var(--grn)', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}
                    >
                      📦 Archive (paid off)
                    </button>
                  )}
                  <button
                    onClick={() => removeCard(c.id)}
                    style={{
                      flex: 1,
                      padding: '6px',
                      background: 'none',
                      border: '1px solid var(--bdr)',
                      borderRadius: 7,
                      color: 'var(--mut)',
                      fontSize: 11,
                      cursor: 'pointer',
                    }}
                  >
                    Remove account
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {/* Archived (paid off) accounts */}
        {archivedCards.length > 0 && (
          <div style={{ marginTop: 4 }}>
            <button
              onClick={() => setShowArchived(s => !s)}
              style={{ background: 'none', border: 'none', color: 'var(--mut)', cursor: 'pointer', fontSize: 12, fontWeight: 700, padding: '4px 0', marginBottom: 6 }}
            >
              {showArchived ? '▼' : '▶'} 📦 {archivedCards.length} archived account{archivedCards.length !== 1 ? 's' : ''} (paid off)
            </button>
            {showArchived && archivedCards.map(c => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--surf2)', borderRadius: 10, border: '1px solid var(--bdr)', marginBottom: 6, opacity: 0.7 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: c.col || 'var(--grn)', flexShrink: 0 }} />
                <div style={{ flex: 1, fontSize: 13, fontWeight: 600, textDecoration: 'line-through', color: 'var(--mut)' }}>{c.name}</div>
                <div style={{ fontSize: 11, color: 'var(--grn)', fontWeight: 700 }}>✓ Paid off</div>
                <button
                  onClick={() => unarchiveCard(c.id)}
                  style={{ background: 'none', border: '1px solid var(--bdr)', borderRadius: 6, color: 'var(--mut)', fontSize: 10, cursor: 'pointer', padding: '3px 8px' }}
                >
                  Restore
                </button>
                <button
                  onClick={() => removeCard(c.id)}
                  style={{ background: 'none', border: 'none', color: 'var(--mut)', cursor: 'pointer', fontSize: 13 }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add new debt card */}
        {adding ? (
          <div className="dc" style={{ borderColor: 'var(--ylw)', borderStyle: 'dashed' }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10, color: 'var(--ylw)' }}>
              + New Debt
            </div>
            <div className="ef">
              <label>Name</label>
              <input
                className="ob-inp"
                value={newCard.name}
                onChange={(e) => setNewCard((n) => ({ ...n, name: e.target.value }))}
                placeholder="e.g. Chase Visa"
              />
            </div>
            <div className="ef">
              <label>Balance ($)</label>
              <input
                type="number"
                className="ob-inp"
                value={newCard.bal}
                onChange={(e) => setNewCard((n) => ({ ...n, bal: e.target.value }))}
                placeholder="0"
              />
            </div>
            <div className="ef">
              <label>APR (%)</label>
              <input
                type="number"
                className="ob-inp"
                value={newCard.apr}
                onChange={(e) => setNewCard((n) => ({ ...n, apr: e.target.value }))}
                placeholder="0.00"
                step=".01"
              />
            </div>
            <div className="ef">
              <label>Monthly Payment ($)</label>
              <input
                type="number"
                className="ob-inp"
                value={newCard.min}
                onChange={(e) => setNewCard((n) => ({ ...n, min: e.target.value }))}
                placeholder="what you actually pay"
              />
            </div>
            <div className="ef">
              <label>Due Day</label>
              <input
                type="number"
                className="ob-inp"
                value={newCard.due}
                onChange={(e) => setNewCard((n) => ({ ...n, due: e.target.value }))}
                placeholder="1-31"
                min="1"
                max="31"
              />
            </div>
            <div className="ef">
              <label>Color</label>
              <div className="ob-cols">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    className={'ob-col' + (newCard.col === c ? ' on' : '')}
                    style={{ background: c }}
                    onClick={() => setNewCard((n) => ({ ...n, col: c }))}
                  />
                ))}
              </div>
            </div>
            <div className="ea">
              <button className="bsave" onClick={addCard}>
                Add
              </button>
              <button className="bcancel" onClick={() => setAdding(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div
            className="dc"
            style={{
              borderStyle: 'dashed',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 120,
              color: 'var(--mut)',
              flexDirection: 'column',
              gap: 8,
            }}
            onClick={() => setAdding(true)}
          >
            <span style={{ fontSize: 24 }}>+</span>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Add debt account</span>
          </div>
        )}
      </div>

      {cards && cards.length > 0 && (
        <>
          <div className="ctrl">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 10,
              }}
            >
              <div className="st" style={{ marginBottom: 0 }}>
                ⚙️ Payoff Strategy
              </div>
              <button
                onClick={() => setDebtCustomOpen((o) => !o)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '4px 9px',
                  background: debtCustomOpen ? 'var(--surf2)' : 'none',
                  border: '1px solid var(--bdr)',
                  borderRadius: 7,
                  fontSize: 11,
                  color: 'var(--mut)',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                ⚙️ Customize {debtCustomOpen ? '▲' : '▼'}
              </button>
            </div>
            {debtCustomOpen && (
              <div
                style={{
                  marginBottom: 12,
                  padding: '10px 12px',
                  background: 'var(--surf)',
                  border: '1px solid var(--bdr)',
                  borderRadius: 9,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: 'var(--mut)',
                    textTransform: 'uppercase',
                    letterSpacing: '.5px',
                    marginBottom: 8,
                  }}
                >
                  Show / Hide Sections
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {[
                    { k: 'showChart', l: 'Balance Chart', v: showChart, fn: setShowChart },
                    {
                      k: 'showSchedule',
                      l: 'Month-by-Month Schedule',
                      v: showSchedule,
                      fn: setShowSchedule,
                    },
                  ].map(({ k, l, v, fn }) => (
                    <button
                      key={k}
                      onClick={() => fn((x) => !x)}
                      style={{
                        padding: '4px 10px',
                        borderRadius: 99,
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                        border: '1px solid ' + (v ? 'var(--grn)' : 'var(--bdr)'),
                        background: v ? 'rgba(107,203,119,.12)' : 'none',
                        color: v ? 'var(--grn)' : 'var(--mut)',
                      }}
                    >
                      {v ? '✓ ' : ''}
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="cg">
              <div>
                <span className="cl">Method</span>
                <div className="mbs">
                  <button
                    className={'mb' + (method === 'snowball' ? ' on' : '')}
                    onClick={() => updateState({ method: 'snowball' })}
                  >
                    <div className="mbt">❄️ Snowball</div>
                    <div className="mbs2">Lowest balance first</div>
                  </button>
                  <button
                    className={'mb' + (method === 'avalanche' ? ' on' : '')}
                    onClick={() => updateState({ method: 'avalanche' })}
                  >
                    <div className="mbt">🏔️ Avalanche</div>
                    <div className="mbs2">Highest APR first</div>
                  </button>
                </div>
                <div className="ip ibl">
                  Priority: {method === 'snowball' ? snowOrder : avalOrder}
                </div>
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span className="cl">Extra Monthly:</span>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      background: 'var(--surf2)',
                      border: '1px solid var(--bdr)',
                      borderRadius: 7,
                      padding: '2px 8px',
                    }}
                  >
                    <span style={{ fontSize: 12, color: 'var(--mut)' }}>$</span>
                    <input
                      type="number"
                      min="0"
                      step="10"
                      value={extra}
                      onChange={(e) => updateState({ extra: parseInt(e.target.value) || 0 })}
                      style={{
                        width: 70,
                        background: 'none',
                        border: 'none',
                        fontSize: 13,
                        fontWeight: 700,
                        color: 'var(--txt)',
                        padding: '4px 0',
                        fontFamily: 'inherit',
                      }}
                    />
                  </div>
                </div>
                <input
                  type="range"
                  min="0"
                  max="2000"
                  step="10"
                  value={Math.min(extra, 2000)}
                  onChange={(e) => updateState({ extra: parseInt(e.target.value) || 0 })}
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 10,
                    color: 'var(--mut)',
                    marginTop: 2,
                  }}
                >
                  <span>$0</span>
                  <span>$2,000+ (type any amount above)</span>
                </div>
                <div className="ip ign">
                  Total/mo: <strong>{f0(totMin + extra)}</strong> · Saves{' '}
                  <strong>{f0(Math.max(0, m.int - r.int))}</strong> in interest
                </div>
              </div>
            </div>
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--bdr)' }}>
              <span className="cl">💸 One-Time Lump Sum</span>
              <div className="lw">
                <span>$</span>
                <input
                  className="li"
                  type="number"
                  defaultValue={lump || ''}
                  min="0"
                  placeholder="0"
                  onChange={(e) => updateState({ lump: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="ip iyl" style={{ marginTop: 7 }}>
                {lump > 0
                  ? 'Applied to ' +
                    (firstP?.name || '') +
                    '. Saves ' +
                    f0(Math.max(0, rNL.int - r.int)) +
                    ', finishes ' +
                    Math.max(0, rNL.mos - r.mos) +
                    ' months earlier.'
                  : 'Enter a one-time payment (tax refund, bonus) to see the impact.'}
              </div>
            </div>
            <div className="ip ibl" style={{ marginTop: 10 }}>
              Total/mo: <strong>{f0(totMin + extra)}</strong> &nbsp;·&nbsp; Debt-free:{' '}
              <strong>{r.end}</strong> &nbsp;·&nbsp; Interest: <strong>{f0(r.int)}</strong>
            </div>
          </div>

          {showChart && (
            <div className="cc">
              <div className="st">Balance Over Time</div>
              <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 10 }}>
                Your plan vs minimums only
              </div>
              <div
                dangerouslySetInnerHTML={{
                  __html: svgLine(merged, [
                    { k: 'plan', c: 'var(--ylw)', w: 2.5, fill: true },
                    { k: 'min', c: '#2e2e40', w: 2, dash: true },
                  ]),
                }}
              />
              <div className="cleg">
                <div className="cli">
                  <svg width="16" height="3">
                    <rect width="16" height="3" rx="2" fill="var(--ylw)" />
                  </svg>
                  {planLbl}
                </div>
                <div className="cli">
                  <svg width="16" height="3">
                    <rect width="16" height="3" rx="2" fill="#2e2e40" />
                  </svg>
                  Minimums Only
                </div>
              </div>
            </div>
          )}

          <div className="pr">
            {[
              { l: 'Interest (Plan)', v: f0(r.int), c: 'var(--red)' },
              { l: 'Interest (Min)', v: f0(m.int), c: 'var(--mut)' },
              { l: 'Interest Saved', v: f0(Math.max(0, m.int - r.int)), c: 'var(--grn)' },
              { l: 'Months Saved', v: Math.max(0, m.mos - r.mos) + ' mo', c: 'var(--blu)' },
            ].map((p) => (
              <div key={p.l} className="pl" style={{ borderLeftColor: p.c }}>
                <div className="pll">{p.l}</div>
                <div className="plv" style={{ color: p.c }}>
                  {p.v}
                </div>
              </div>
            ))}
          </div>

          {showSchedule && (
            <div className="cc">
              <div className="st">Month-by-Month Schedule</div>
              <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 10 }}>
                Projection from current balances — update actuals monthly in Check-In for best
                accuracy.
              </div>
              <div className="tw">
                <table>
                  <thead className="sth">
                    <tr>
                      <th>Month</th>
                      {(cards || []).map((c) => (
                        <th key={c.id}>{c.name}</th>
                      ))}
                      <th>Total Owed</th>
                      <th>Interest This Month</th>
                    </tr>
                  </thead>
                  <tbody className="stb">
                    {r.sched.map((row, i) => (
                      <tr key={i}>
                        <td>{row.lbl}</td>
                        {(cards || []).map((c) => (
                          <td key={c.id} className={row[c.id] === 0 ? 'paid' : ''}>
                            {row[c.id] === 0 ? '✓ PAID' : f0(row[c.id])}
                          </td>
                        ))}
                        <td>
                          <strong>{f0(row.tot)}</strong>
                        </td>
                        <td style={{ color: row.mi > 0 ? 'var(--red)' : 'var(--grn)' }}>
                          {row.mi > 0 ? f0(row.mi) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
